import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, ChevronDown } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

const Header = () => {
  const location = useLocation();
  const [isOpen, setIsOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [showServicesDropdown, setShowServicesDropdown] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState('cybersecurity');
  const servicesRef = useRef<HTMLDivElement>(null);
  const timeoutRef = useRef<ReturnType<typeof setTimeout>>();

  const isOffensiveSecurityPage = location.pathname === '/services/cybersecurity/offensive-security';
  const isNetworkEngineeringPage = location.pathname === '/services/infrastructure/network-engineering';
  const isDataCenterPage = location.pathname === '/services/infrastructure/data-center-design';
  const isServerAdministrationPage = location.pathname === '/services/infrastructure/server-administration';
  const isAboutPage = location.pathname === '/about';
  const isBlueThemePage = isNetworkEngineeringPage || isDataCenterPage || isAboutPage;

  const toggleMenu = () => {
    setIsOpen(!isOpen);
  };

  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 50) {
        setIsScrolled(true);
      } else {
        setIsScrolled(false);
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleServicesMouseEnter = () => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    setShowServicesDropdown(true);
  };

  const handleServicesMouseLeave = () => {
    timeoutRef.current = setTimeout(() => {
      setShowServicesDropdown(false);
    }, 150);
  };

  // Reverted to white background on scroll as per user request
  const navBgClass = (showServicesDropdown || isScrolled) 
    ? 'bg-white shadow-lg' 
    : 'bg-transparent';

  const headerClasses = `fixed w-full z-50 transition-all duration-300 ${navBgClass}`;

  const servicesData = {
    cybersecurity: {
      title: 'Cybersecurity Solutions',
      services: [
        {
          title: 'Defensive Security',
          subtitle: 'XDR, EDR, SIEM, IDS, IPS and real-time defense systems',
          icon: (
            <svg className="w-12 h-12 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
            </svg>
          ),
          link: '/services/cybersecurity/defensive-security'
        },
        {
          title: 'Offensive Security',
          subtitle: 'Advanced security assessment & penetration testing',
          icon: (
            <svg className="w-12 h-12 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
            </svg>
          ),
          link: '/services/cybersecurity/offensive-security'
        },
        {
          title: 'SOC as a Service',
          subtitle: '24/7 Security Operations Center',
          icon: (
            <svg className="w-12 h-12 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
            </svg>
          ),
          link: '/services/cybersecurity/soc-as-a-service'
        }
      ]
    },
    infrastructure: {
      title: 'Infrastructure Solutions',
      services: [
        {
          title: 'Data Center Design',
          subtitle: 'Next-generation data center architecture',
          icon: (
            <svg className="w-12 h-12 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
            </svg>
          ),
          link: '/services/infrastructure/data-center-design'
        },
        {
          title: 'Network Engineering',
          subtitle: 'Enterprise network design and implementation',
          icon: (
            <svg className="w-12 h-12 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 12h14M5 12a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v4a2 2 0 01-2 2M5 12a2 2 0 00-2 2v4a2 2 0 002 2h14a2 2 0 002-2v-4a2 2 0 00-2-2m-2-4h.01M17 16h.01" />
            </svg>
          ),
          link: '/services/infrastructure/network-engineering'
        },
        {
          title: 'Cloud Integration',
          subtitle: 'Hybrid and multi-cloud solutions',
          icon: (
            <svg className="w-12 h-12 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M9 19l3 3m0 0l3-3m-3 3V10" />
            </svg>
          ),
          link: '/services/infrastructure/cloud-integration'
        },
        {
          title: 'Server Administration',
          subtitle: 'Expert server management and support',
          icon: (
            <svg className="w-12 h-12 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 12h14M5 12a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v4a2 2 0 01-2 2M5 12a2 2 0 00-2 2v4a2 2 0 002 2h14a2 2 0 002-2v-4a2 2 0 00-2-2" />
            </svg>
          ),
          link: '/services/infrastructure/server-administration'
        }
      ]
    }
  };

  const categories = [
    { id: 'cybersecurity', label: 'Cybersecurity' },
    { id: 'infrastructure', label: 'Infrastructure' }
  ];

  return (
    <header className={headerClasses}>
      <div className="w-full max-w-[1440px] mx-auto px-2 flex items-center h-24 md:h-[7.5rem]">
        <Link to="/" className="flex items-center space-x-3 mr-16 lg:mr-24 py-2">
          <img
            src={
              (showServicesDropdown || isScrolled || isBlueThemePage)
                ? (location.pathname.includes('/services/cybersecurity/offensive-security')
                  ? "/Logos/atlasdefenders_red_logo.svg"
                  : (location.pathname.includes('/services/cybersecurity/soc-as-a-service') || location.pathname.includes('/services/cybersecurity/defensive-security')
                    ? "/Logos/atlasdefenders_blue_logo.svg"
                    : "/Logos/atlasdefenders_normal_logo.svg"))
                : "/Logos/atlasdefenders_white_logo.svg"
            }
            alt="Atlas Defenders"
            className="h-20 md:h-[6.5rem] w-auto transition-all duration-300"
          />
        </Link>

        <nav className="hidden md:flex items-center justify-center space-x-8 flex-1">
          <Link
            to="/"
            className={`font-medium transition-colors duration-300 mr-6 ${showServicesDropdown || isScrolled ? (isOffensiveSecurityPage ? 'text-gray-900 hover:text-red-600' : 'text-gray-900 hover:text-blue-600') : isBlueThemePage ? 'text-gray-900 hover:text-gray-700' : 'text-white hover:text-accent'}`}>
            Home
          </Link>
          <div
            className="relative"
            ref={servicesRef}
            onMouseEnter={handleServicesMouseEnter}
            onMouseLeave={handleServicesMouseLeave}
          >
            <button
              className={`font-medium transition-colors duration-300 flex items-center ${showServicesDropdown || isScrolled ? (isOffensiveSecurityPage ? 'text-gray-900 hover:text-red-600' : 'text-gray-900 hover:text-blue-600') : isBlueThemePage ? 'text-gray-900 hover:text-gray-700' : 'text-white hover:text-accent'}`}>
              Services
              <ChevronDown size={16} className={`ml-1 transition-transform duration-300 ${showServicesDropdown ? 'rotate-180' : ''}`} />
            </button>

            <AnimatePresence>
              {showServicesDropdown && (
                <motion.div
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  transition={{ duration: 0.2 }}
                  className="fixed left-0 top-24 md:top-[7.5rem] w-full bg-white shadow-xl z-40 overflow-hidden"
                  style={{ height: '320px' }}
                >
                  <div className="flex h-full">
                    <div className="w-56 bg-gray-50 border-r border-gray-200 h-full">
                      <div className="p-6">
                        <h3 className="text-lg font-bold text-gray-900 mb-4">Our Services</h3>
                        <nav className="space-y-1">
                          {categories.map((category) => (
                            <button
                              key={category.id}
                              onMouseEnter={() => setSelectedCategory(category.id)}
                              className={`w-full text-left px-3 py-2 text-sm font-medium transition-all duration-150 relative ${selectedCategory === category.id ? (isOffensiveSecurityPage ? 'text-red-600 border-r-2 border-red-600' : 'text-blue-600 border-r-2 border-blue-600') : (isOffensiveSecurityPage ? 'text-gray-700 hover:text-red-600 hover:border-r-2 hover:border-red-600' : 'text-gray-700 hover:text-blue-600 hover:border-r-2 hover:border-blue-600')}`}>
                              {category.label}
                            </button>
                          ))}
                        </nav>
                      </div>
                    </div>

                    <div className="flex-1 p-8 bg-white relative overflow-hidden">
                      <div className="flex items-center justify-between mb-6">
                        <h2 className="text-xl font-normal text-gray-900">
                          {servicesData[selectedCategory as keyof typeof servicesData].title}
                        </h2>
                      </div>

                      <div className={`grid ${servicesData[selectedCategory as keyof typeof servicesData].services.length === 4 ? 'grid-cols-4' : 'grid-cols-3'} gap-8`}>
                        {servicesData[selectedCategory as keyof typeof servicesData].services.map((service, index) => (
                          <Link
                            key={index}
                            to={service.link}
                            onClick={() => setShowServicesDropdown(false)}
                            className="group flex flex-col items-center text-center p-4 rounded-lg hover:bg-gray-50 transition-all duration-300"
                          >
                            <div className="mb-4">
                              {React.cloneElement(service.icon, {
                                className: `w-12 h-12 ${isOffensiveSecurityPage ? 'text-red-500' : 'text-blue-500'}`
                              })}
                            </div>
                            <h3 className="text-base font-normal text-gray-900 mb-2">
                              {service.title}
                            </h3>
                            <p className="text-sm text-gray-600 mb-3">
                              {service.subtitle}
                            </p>
                            <span className={`text-sm font-medium group-hover:underline ${isOffensiveSecurityPage ? 'text-red-600' : 'text-blue-600'}`}>
                              Learn More →
                            </span>
                          </Link>
                        ))}
                      </div>
                    </div>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          <Link
            to="/industries"
            className={`font-medium transition-colors duration-300 ${showServicesDropdown || isScrolled ? (isOffensiveSecurityPage ? 'text-gray-900 hover:text-red-600' : 'text-gray-900 hover:text-blue-600') : isBlueThemePage ? 'text-gray-900 hover:text-gray-700' : 'text-white hover:text-accent'}`}>
            Industries
          </Link>
          <Link
            to="/about"
            className={`font-medium transition-colors duration-300 ${showServicesDropdown || isScrolled ? (isOffensiveSecurityPage ? 'text-gray-900 hover:text-red-600' : 'text-gray-900 hover:text-blue-600') : isBlueThemePage ? 'text-gray-900 hover:text-gray-700' : 'text-white hover:text-accent'}`}>
            About
          </Link>
          <Link
            to="/contact"
            className={`font-medium transition-colors duration-300 ${showServicesDropdown || isScrolled ? (isOffensiveSecurityPage ? 'text-gray-900 hover:text-red-600' : 'text-gray-900 hover:text-blue-600') : isBlueThemePage ? 'text-gray-900 hover:text-gray-700' : 'text-white hover:text-accent'}`}>
            Contact
          </Link>
        </nav>

        <div className="hidden md:block ml-8">
          <Link
            to="/contact"
            className={`inline-flex items-center justify-center px-8 py-3 text-sm font-semibold transition-all duration-300 shadow-lg hover:shadow-xl transform hover:scale-105 rounded-full ${isOffensiveSecurityPage ? (isScrolled ? 'bg-gradient-to-r from-red-500 to-red-600 hover:from-red-600 hover:to-red-700 text-white' : 'bg-white text-red-600 hover:bg-gray-50') : (isScrolled || isBlueThemePage || isServerAdministrationPage || isAboutPage ? 'bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white' : 'bg-white text-[#001F5F] hover:bg-gray-100')}`}>
            Get Consultation
          </Link>
        </div>

        <button
          onClick={toggleMenu}
          className={`md:hidden p-2 rounded-md ${showServicesDropdown || isScrolled ? 'text-gray-900' : isNetworkEngineeringPage ? 'text-gray-900' : 'text-white'}`}>
          {isOpen ? <X size={24} /> : <Menu size={24} />}
        </button>
      </div>

      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="md:hidden bg-white border-t border-gray-200 shadow-lg"
          >
            <div className="container mx-auto px-4 py-4">
              <nav className="space-y-4">
                <Link to="/services" className={`block font-medium ${isOffensiveSecurityPage ? 'text-text-dark hover:text-red-600' : 'text-text-dark hover:text-secondary'}`} onClick={() => setIsOpen(false)}>
                  Services
                </Link>
                <Link to="/industries" className={`block font-medium ${isOffensiveSecurityPage ? 'text-text-dark hover:text-red-600' : 'text-text-dark hover:text-secondary'}`} onClick={() => setIsOpen(false)}>
                  Industries
                </Link>
                <Link to="/about" className={`block font-medium ${isOffensiveSecurityPage ? 'text-text-dark hover:text-red-600' : 'text-text-dark hover:text-secondary'}`} onClick={() => setIsOpen(false)}>
                  About
                </Link>
                <Link to="/contact" className={`block font-medium ${isOffensiveSecurityPage ? 'text-text-dark hover:text-red-600' : 'text-text-dark hover:text-secondary'}`} onClick={() => setIsOpen(false)}>
                  Contact
                </Link>
                <Link
                  to="/contact"
                  className={`block px-6 py-3 rounded-xl font-semibold transition-all duration-300 text-center mt-4 ${isOffensiveSecurityPage ? 'bg-gradient-to-r from-red-600 to-red-800 text-white hover:from-red-700 hover:to-red-900' : 'bg-gradient-to-r from-blue-600 to-indigo-700 text-white hover:from-blue-700 hover:to-indigo-800'}`}
                  onClick={() => setIsOpen(false)}
                >
                  Get Consultation
                </Link>
              </nav>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};

export default Header;
